<template>
  <div class="modal--content potReactoinModal">
    <div class="modalInnerContent">
      <!-- tabs header -->
      <div class="headerDv justify-content-between">
        <div class="tabs-header" v-if="hasReactionData">
          <div
            v-for="tab in reactionTabs"
            :key="tab.type"
            class="tabs-header-item"
            :class="{ active: activeTab === tab.type, disabled: connectionsLoading }"
            @click="!connectionsLoading && setActiveTab(tab.type)"
          >
            <figure v-if="tab.type !== 'all'" class="reaction-icon">
              <img
                :src="require(`@/assets/images/reactions/${tab.type}.png`)"
                :alt="tab.label"
              />
            </figure>
            <p v-if="tab.type !== 'all'">({{ tab.count }})</p>
            <p v-else>All ({{ tab.count }})</p>
          </div>
          <div></div>
        </div>
        <IconComponent
          @click="closeModal()"
          class="newHeadericon"
          icon="close-times.svg"
        />
      </div>
      <!-- tabs content -->
      <div class="users-list">
        <div v-if="connectionsLoading" class="loading-state">
          <div class="loader"></div>
          <span>Loading reactions...</span>
        </div>
        <div v-else-if="filteredReactions.length === 0" class="no-data-state">
          <span>No reactions found</span>
        </div>
        <template v-else>
        <div
          v-for="reaction in filteredReactions"
          :key="reaction._id"
          class="user-row"
        >
          <div class="user-row-left" @click="goToProfile(reaction.userName)">
            <ProfileAvatarComponent
              class="small"
              :profileImage="
                getUserProfileImage(
                  reaction.userId,
                  reaction.userProfilePicture || 0
                )
              "
              :userId="reaction.userId"
              :border="true"
              :badges="false"
              :camera="false"
              :showReaction="true"
              :reaction="reaction.reactionType"
            />
            <p class="user-name">{{ reaction.userFullName }}</p>
            <p class="user-handle">@{{ reaction.userName }}</p>
          </div>
          <div
            class="ButtonDv"
            v-if="currentUser && currentUser._id !== reaction.userId"
          >
            <a v-if="userLoadingStates[reaction.userId]" class="follow-btn">
              <div class="loader follow-loader" style="margin: 0px auto"></div>
            </a>
            <a
              v-else-if="!reaction.isFollowing"
              class="follow-btn"
              @click="followUser(reaction)"
            >
              <IconComponent icon="icon-follow-button.svg" />
              Follow</a
            >
            <a
              v-else
              class="following-btn"
              @click="unfollowUser(reaction.userId)"
            >
              <IconComponent
                class="unfollowUser"
                icon="icon-button-unfollowing.svg"
              />
              Following</a
            >
            <!-- <a v-else-if="!reaction.isFollowing" class="follow-btn" @click="followUser(reaction)">
                    <IconComponent  icon="icon-follow-button.svg" />
                    Follow</a
                >
                <a v-else class="following-btn" @click="unfollowUser(reaction.userId)">
                    <IconComponent class="unfollowUser" icon="icon-button-unfollowing.svg" />
                    Following</a
                > -->
          </div>
          <div class="ButtonDv" v-else>
            <a class="following-btn"> You </a>
          </div>
        </div>
        <div v-if="hasMore" class="load-more-container">
          <button
            class="load-more-btn"
            :disabled="loadMoreLoading"
            @click="loadMoreReactions"
          >
            <span v-if="loadMoreLoading" class="loader load-more-loader"></span>
            <span v-else>{{ t("common.actions.loadMore") }}</span>
          </button>
        </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import IconComponent from "@/components/IconComponent.vue";
import ProfileAvatarComponent from "@/components/ProfileAvatarComponent.vue";
import utilService from "@/services/util.service";
import { popModal } from "jenesius-vue-modal";
import httpService from "@/services/http.service";
import storageService from "@/services/storage.service";
import { ConnectionComposables } from "@/composables/connection/connection.composable";
import router from "@/router/router";
import { RouteEnum } from "@/router/routeNames";
import { useReactionStore } from "@/services/reaction.store";
import { useI18n } from "vue-i18n";

const PAGE_SIZE = 10;

const props = defineProps<{
  sourceId: string;
}>();

const reactions = ref<any[]>([]);
const connectionsLoading = ref(false);
const loadMoreLoading = ref(false);
const activeTab = ref("all");
const currentPage = ref(1);
const hasMore = ref(false);
const { onFollow, unfollow } = ConnectionComposables();
const reactionStore = useReactionStore();
const { t } = useI18n();

// Individual loading states for each user
const userLoadingStates = ref<{ [key: string]: boolean }>({});

const currentUser = computed(() => {
  if (storageService.user.value && storageService.user.value._id) {
    return storageService.user.value;
  }
  return null;
});

const goToProfile = (data: any) => {
  closeModal();
  router.push({
    name: RouteEnum.profile,
    params: { username: data },
  });
};

const reactionCounts = computed(() =>
  reactionStore.getAllReactionCountsForPost(props.sourceId)
);

const totalReactionCount = computed(() =>
  Object.values(reactionCounts.value).reduce(
    (sum, count) => sum + (count || 0),
    0
  )
);

const hasReactionData = computed(
  () => totalReactionCount.value > 0 || reactions.value.length > 0
);

// Reaction tabs configuration
const reactionTabs = computed(() => {
  const tabs = [
    { type: "all", label: "All", count: totalReactionCount.value },
  ];

  Object.entries(reactionCounts.value).forEach(([type, count]) => {
    if (count > 0) {
      tabs.push({
        type,
        label: getReactionLabel(type),
        count,
      });
    }
  });

  return tabs;
});

// Function to get human-readable labels for reaction types
const getReactionLabel = (type: string): string => {
  const labels: { [key: string]: string } = {
    like: "Like",
    sadbull: "Sad Bull",
    heart: "Heart",
    angrybull: "Angry Bull",
    shock: "Shock",
    shocked: "Shocked",
    smirkingbull: "Smirking Bull",
    appreciated: "Appreciated",
    bloodbath: "Bloodbath",
    crapmine: "Crap Mine",
    crashingdown: "Crashing Down",
    cryptomarket: "Crypto Market",
    deadweight: "Dead Weight",
    diamondhand: "Diamond Hand",
    downchart: "Down Chart",
    feelingsick: "Feeling Sick",
    allgreen: "All Green",
    goldmine: "Gold Mine",
    kingofthemarket: "King of Market",
    kiss: "Kiss",
    queenofthemarket: "Queen of Market",
    tothemood: "To The Mood",
    upchart: "Up Chart",
  };
  return labels[type] || type.charAt(0).toUpperCase() + type.slice(1);
};

const buildFetchParams = (page: number) => {
  const params: { page: number; count: number; type?: string } = {
    page,
    count: PAGE_SIZE,
  };

  if (activeTab.value !== "all") {
    params.type = activeTab.value;
  }

  return params;
};

const fetchReactions = async (append = false) => {
  try {
    if (append) {
      loadMoreLoading.value = true;
    } else {
      connectionsLoading.value = true;
    }

    const page = append ? currentPage.value + 1 : 1;
    const response = await httpService.getDetailedReactions(
      props.sourceId,
      buildFetchParams(page)
    );
    const items = response || [];

    if (append) {
      currentPage.value = page;
      reactions.value = [...reactions.value, ...items];
    } else {
      currentPage.value = 1;
      reactions.value = items;
    }

    hasMore.value = items.length === PAGE_SIZE;
  } catch (e) {
    console.log("error", e);
    if (!append) {
      reactions.value = [];
    }
    hasMore.value = false;
  } finally {
    connectionsLoading.value = false;
    loadMoreLoading.value = false;
  }
};

// Set active tab
const setActiveTab = async (tabType: string) => {
  if (activeTab.value === tabType) return;
  activeTab.value = tabType;
  await fetchReactions(false);
};

const loadMoreReactions = () => fetchReactions(true);

// API already filters by type when a tab is selected
const filteredReactions = computed(() => reactions.value);

// Follow/Unfollow functions
const followUser = async (profile: any) => {
  console.log("followUser", profile);
  document.body.click();
  userLoadingStates.value[profile.userId] = true; // Set loading state for the specific user
  try {
    await onFollow(profile);
  } catch (error) {
    console.error("Error following user:", error);
  } finally {
    userLoadingStates.value[profile.userId] = false; // Reset loading state after completion
  }
};

const unfollowUser = async (userId: string) => {
  document.body.click();
  userLoadingStates.value[userId] = true; // Set loading state for the specific user
  try {
    await unfollow(userId);
  } catch (error) {
    console.error("Error unfollowing user:", error);
  } finally {
    userLoadingStates.value[userId] = false; // Reset loading state after completion
  }
};

onMounted(async () => {
  await fetchReactions(false);
});

// Store user images like in TopPeopleComponent
const userImages = ref<{ [key: string]: string }>({});

// Function to get user profile image using utilService
const getUserProfileImage = (userId: string, profilePicture: number) => {
  if (!userImages.value[userId]) {
    userImages.value[userId] = utilService.getUserProfilePicture(
      userId,
      profilePicture,
      "male",
      false,
      false
    );
  }
  return userImages.value[userId];
};

const closeModal = () => popModal();
</script>

<style lang="scss">
.potReactoinModal {
  max-width: 600px;
  min-width: 600px;
  width: 600px;
  height: auto;
  border-radius: 20px;
  padding: 20px;
  padding-bottom: 0;
}

.modalInnerContent {
  width: 100%;
  min-width: 100%;
}

.tabs-header {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  @include hideScrollbar();
  margin-inline-end: 15px;
}
.headerDv {
  margin-bottom: 20px;
  .newHeadericon {
    color: var(--base-text-white);
    background: var(--base-light);
    border-radius: 4px;
    padding: 4px;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
  }
}

.tabs-header-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  background: transparent;
  border-bottom: 2px solid transparent;
  padding-bottom: 5px;
  font-size: var(--font-2);
  &.active {
    border-bottom: 2px solid var(--color-warning);
  }

  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .reaction-icon {
    width: 20px;
    height: 26px;
    margin: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  p {
    margin: 0;
    font-size: var(--font-sm);
    font-weight: 500;
  }
}

.users-list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 600px;
  height: 600px;
  overflow-y: auto;
  @include scrollbar();
  @media only screen and (min-width: 1280px) and (max-width: 1891px) {
    max-height: 500px;
    height: 500px;
  }
}

.user-row {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--base-light);
  border-radius: 10px;
  padding: 8px 15px;
  padding-top: 5px;
  padding-bottom: 6px;
  cursor: pointer;
  transition: background 0.2s;
  position: relative;
  padding-bottom: 4px;

  .user-row-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
  }
  .follow-loader {
    width: 18px;
    height: 18px;
    border: 3px solid #79757f;
    border-top: 3px solid #999b9f;
    margin: 0px auto;
  }

  .user-row-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 15px;
  }

  .user-name {
    color: var(--base-text-white);
    font-size: var(--font-2sm);
    font-weight: 500;
    margin-top: -4px;
  }

  .user-handle {
    color: #999b9f;
    font-size: var(--font-xs);
    font-weight: 500;
    margin-top: -4px;
  }

  .follow-btn,
  .following-btn {
    border-radius: 6px;
    border: none;
    cursor: pointer !important;
    font-size: var(--font-2xs);
    transition: all 0.2s;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 5px;
    padding: 4px 16px;
    font-weight: 500;
    .svg_icon {
      width: 14px;
      height: 14px;
    }
  }

  .follow-btn {
    background: var(--base-lightest);
    color: var(--base-text);
  }

  .following-btn {
    background: transparent;
    color: #79757f;
  }
}

.loading-state,
.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 600px;
  color: var(--base-text-white);
  gap: 15px;

  @media only screen and (min-width: 1280px) and (max-width: 1891px) {
    height: 400px;
  }
}

//   .loader {
//     width: 30px;
//     height: 30px;
//     border: 3px solid var(--base-light);
//     border-top: 3px solid var(--new-theme-color);
//     border-radius: 50%;
//     animation: spin 1s linear infinite;
//   }

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.load-more-container {
  display: flex;
  justify-content: center;
  padding: 12px 0 16px;
}

.load-more-btn {
  background: var(--base-lightest);
  color: var(--base-text);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: var(--font-2xs);
  font-weight: 500;
  padding: 8px 24px;
  transition: opacity 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}

.load-more-loader {
  width: 18px;
  height: 18px;
  border: 3px solid #79757f;
  border-top: 3px solid #999b9f;
  display: inline-block;
}
</style>
